import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import {
  api,
  type AuthorContext,
  type ChapterQuestions,
  type FoodDrinkPairing,
  type KeyPassage,
  type Meeting,
  type Rsvp,
  type Theme,
  type VenueSuggestion,
} from "../api";
import { Spinner } from "../components/ui";

// Day-of host packet — print-friendly agenda pulled from existing AI cache
// + RSVPs + dietary notes. Designed to fit comfortably on 1–2 pages when
// printed; on screen it's a tall scrollable page.

export default function MeetingPacketPage() {
  const { id } = useParams<{ id: string }>();
  const meetingId = Number(id);

  const meetings = useQuery({
    queryKey: ["meetings"],
    queryFn: () => api.get<{ meetings: Meeting[] }>("/meetings"),
    enabled: !!meetingId,
  });

  const meeting = meetings.data?.meetings.find((m) => m.id === meetingId) ?? null;
  const bookId = meeting?.bookId ?? null;

  const rsvps = useQuery({
    queryKey: ["rsvps", meetingId],
    queryFn: () => api.get<{ rsvps: Rsvp[] }>(`/meetings/${meetingId}/rsvps`),
    enabled: !!meetingId,
  });

  const themes = useAiSection<Theme[]>(bookId, "themes");
  const questions = useAiSection<ChapterQuestions[]>(bookId, "questions");
  const passages = useAiSection<KeyPassage[]>(bookId, "passages");
  const pairings = useAiSection<FoodDrinkPairing>(bookId, "pairings");
  const venue = useAiSection<VenueSuggestion>(bookId, "venue");
  const context = useAiSection<AuthorContext>(bookId, "context");

  if (meetings.isLoading) return <Spinner />;
  if (!meeting) return <p className="text-plum-soft">Meeting not found.</p>;

  const date = parseISO(meeting.meetsAt);
  const yes = (rsvps.data?.rsvps ?? []).filter((r) => r.status === "yes");
  const maybe = (rsvps.data?.rsvps ?? []).filter((r) => r.status === "maybe");
  const no = (rsvps.data?.rsvps ?? []).filter((r) => r.status === "no");
  const dietary = (rsvps.data?.rsvps ?? []).filter((r) => r.dietary && r.dietary.trim());

  // Take a representative slice of questions: pick first 2 from each chapter,
  // capped at 10 total. This produces a tight, host-friendly list.
  const pickedQuestions: { chapter: number; title: string | null; q: string }[] = [];
  for (const ch of questions.data ?? []) {
    for (const q of ch.questions.slice(0, 2)) {
      pickedQuestions.push({ chapter: ch.number, title: ch.title, q });
      if (pickedQuestions.length >= 10) break;
    }
    if (pickedQuestions.length >= 10) break;
  }

  return (
    <div className="packet-page max-w-3xl mx-auto">
      <PrintStyles />

      {/* Toolbar (hidden in print) */}
      <div className="no-print flex items-center justify-between mb-6">
        <Link to="/calendar" className="text-sm text-plum-mute hover:text-rose">
          ← Calendar
        </Link>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rose text-white text-sm hover:bg-rose-dark"
        >
          <PrintIcon /> Print packet
        </button>
      </div>

      {/* Cover header */}
      <header className="text-center mb-8 pb-6 border-b border-cream-300">
        <div className="text-[0.65rem] uppercase tracking-[0.28em] text-gold mb-2">
          Host packet
        </div>
        <h1 className="font-serif text-4xl text-plum tracking-tight leading-tight">
          {meeting.bookTitle ?? "A book club meeting"}
        </h1>
        <p className="font-serif italic text-lg text-plum-soft mt-3">
          {format(date, "EEEE, MMMM d, yyyy 'at' h:mm a")}
        </p>
        {meeting.venueName ? (
          <p className="text-plum-soft mt-1">
            {meeting.venueName}
            {meeting.venueAddress ? <> · {meeting.venueAddress}</> : null}
          </p>
        ) : null}
      </header>

      {/* Suggested timing */}
      <Section title="Suggested timing">
        <ol className="flex flex-col gap-1.5 text-plum">
          <TimingRow t="0:00" what="Arrive, drinks, catch up" />
          <TimingRow t="0:20" what="First impressions — go around the room" />
          <TimingRow t="0:40" what="Themes (10 min each)" />
          <TimingRow t="1:00" what="Chapter walk-through & discussion questions" />
          <TimingRow t="1:30" what="Open conversation, key passages read aloud" />
          <TimingRow t="1:50" what="Wrap, ratings, next pick" />
        </ol>
      </Section>

      {/* Attendance */}
      <Section title="Who's coming">
        <div className="grid grid-cols-3 gap-5">
          <AttendanceList label="Yes" count={yes.length} list={yes} tone="rose" />
          <AttendanceList label="Maybe" count={maybe.length} list={maybe} tone="gold" />
          <AttendanceList label="No" count={no.length} list={no} tone="muted" />
        </div>
        {dietary.length > 0 ? (
          <div className="mt-5 pt-4 border-t border-cream-200">
            <h4 className="text-[0.7rem] uppercase tracking-[0.18em] text-gold-dark mb-2">
              Dietary notes
            </h4>
            <ul className="flex flex-col gap-1 text-sm text-plum">
              {dietary.map((d) => (
                <li key={d.userId}>
                  <span className="font-medium">
                    {d.name ?? d.email.split("@")[0]}:
                  </span>{" "}
                  <span className="text-plum-soft italic">{d.dietary}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </Section>

      {/* Context (author / moment) */}
      {context.data ? (
        <Section title="Setting the stage">
          <p className="font-serif text-base leading-relaxed text-plum">
            {context.data.blurb}
          </p>
          {context.data.notableHonors?.length ? (
            <p className="mt-2 text-xs text-plum-mute italic">
              {context.data.notableHonors.join(" · ")}
            </p>
          ) : null}
        </Section>
      ) : null}

      {/* Themes */}
      {themes.data?.length ? (
        <Section title="Themes to explore">
          <ol className="flex flex-col gap-4">
            {themes.data.map((t, i) => (
              <li key={i}>
                <div className="font-serif text-lg text-plum">
                  {i + 1}. {t.title}
                </div>
                <p className="text-plum-soft text-sm leading-relaxed mt-1">{t.description}</p>
              </li>
            ))}
          </ol>
        </Section>
      ) : null}

      {/* Discussion questions */}
      {pickedQuestions.length > 0 ? (
        <Section title="Discussion questions">
          <ol className="flex flex-col gap-3 list-none">
            {pickedQuestions.map((q, i) => (
              <li key={i} className="flex gap-3">
                <span className="font-serif italic text-rose shrink-0 w-6 text-right">
                  {i + 1}.
                </span>
                <div className="flex-1">
                  <p className="text-plum">{q.q}</p>
                  <p className="text-[0.7rem] text-plum-mute mt-0.5 uppercase tracking-[0.12em]">
                    Chapter {q.chapter}
                    {q.title ? ` · ${q.title}` : ""}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </Section>
      ) : null}

      {/* Key passages */}
      {passages.data?.length ? (
        <Section title="Passages to read aloud">
          <div className="flex flex-col gap-5">
            {passages.data.slice(0, 3).map((p, i) => (
              <div key={i}>
                <blockquote className="font-serif italic text-lg text-plum leading-snug pl-4 border-l-2 border-blush">
                  "{p.quote}"
                </blockquote>
                <p className="text-[0.7rem] text-plum-mute mt-1.5 uppercase tracking-[0.14em]">
                  {p.location}
                </p>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {/* Venue */}
      {meeting.venueAddress || venue.data ? (
        <Section title="Where we're meeting">
          {meeting.venueName ? (
            <p className="font-serif text-lg text-plum">{meeting.venueName}</p>
          ) : venue.data ? (
            <p className="font-serif text-lg text-plum">
              {venue.data.name} <span className="text-plum-mute italic font-sans text-sm">— {venue.data.town}, NJ</span>
            </p>
          ) : null}
          {meeting.venueAddress ? (
            <p className="text-plum-soft text-sm mt-1">{meeting.venueAddress}</p>
          ) : null}
          {meeting.notes ? (
            <p className="text-plum-soft text-sm mt-2 italic">{meeting.notes}</p>
          ) : null}
        </Section>
      ) : null}

      {/* Pairings */}
      {pairings.data ? (
        <Section title="On the table & in the glass">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="text-[0.7rem] uppercase tracking-[0.18em] text-gold-dark mb-2">
                Food
              </h4>
              <ul className="flex flex-col gap-2 text-sm text-plum">
                {pairings.data.food.map((f) => (
                  <li key={f.name}>
                    <span className="font-medium">{f.name}</span>
                    <span className="text-plum-soft"> — {f.description}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-[0.7rem] uppercase tracking-[0.18em] text-gold-dark mb-2">
                Drinks
              </h4>
              <ul className="flex flex-col gap-3 text-sm text-plum">
                {pairings.data.drinks.map((d) => (
                  <li key={d.name}>
                    <span className="font-medium">{d.name}</span>
                    <span className="text-plum-soft"> — {d.description}</span>
                    <p className="mt-1 text-[0.7rem] font-mono text-plum-mute">
                      {d.recipe}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      ) : null}

      <footer className="mt-10 pt-6 border-t border-cream-300 text-center text-xs text-plum-mute uppercase tracking-[0.22em]">
        The Book Club
      </footer>
    </div>
  );
}

function useAiSection<T>(bookId: number | null, type: string) {
  return useQuery({
    queryKey: ["ai", bookId ?? 0, type],
    queryFn: () =>
      api.get<{ content: { type: string; data: T; generatedAt: string } | null }>(
        `/ai/${bookId}/${type}`
      ),
    enabled: !!bookId,
    select: (r) => r.content?.data ?? null,
  });
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8 break-inside-avoid">
      <h2 className="font-serif text-xl text-plum tracking-tight mb-3 pb-2 border-b border-cream-200">
        {title}
      </h2>
      {children}
    </section>
  );
}

function TimingRow({ t, what }: { t: string; what: string }) {
  return (
    <li className="flex gap-4 text-sm">
      <span className="font-mono text-gold-dark w-12 shrink-0">{t}</span>
      <span className="text-plum">{what}</span>
    </li>
  );
}

function AttendanceList({
  label,
  count,
  list,
  tone,
}: {
  label: string;
  count: number;
  list: Rsvp[];
  tone: "rose" | "gold" | "muted";
}) {
  const accent =
    tone === "rose" ? "text-rose-dark" : tone === "gold" ? "text-gold-dark" : "text-plum-mute";
  return (
    <div>
      <div className={`text-[0.7rem] uppercase tracking-[0.18em] ${accent} mb-2`}>
        {label} · {count}
      </div>
      <ul className="text-sm text-plum flex flex-col gap-0.5">
        {list.map((r) => (
          <li key={r.userId} className="truncate">
            {r.name ?? r.email.split("@")[0]}
          </li>
        ))}
        {list.length === 0 ? <li className="italic text-plum-mute">—</li> : null}
      </ul>
    </div>
  );
}

function PrintIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M6 9V3h12v6M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v7H6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function PrintStyles() {
  return (
    <style>{`
      @media print {
        body { background: white !important; }
        .no-print { display: none !important; }
        header, main, footer { background: white !important; }
        .packet-page { padding: 0; max-width: 100%; }
        section { page-break-inside: avoid; }
      }
    `}</style>
  );
}
